import type { RecordView } from '@kit/sync/types/record-view';
import { Pin } from 'lucide-react';
import type { Note } from '@/entities/note/schemas/note';
import { PendingDot } from '@/entities/shared/record/pending-dot';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatTimestamp } from '@/shared/lib/dates/format-timestamp';
import { Badge } from '@/shared/ui/badge/badge';

type NoteCardProps = {
  readonly view: RecordView<Note>;
  readonly onOpen: () => void;
};

export function NoteCard({ view, onOpen }: NoteCardProps) {
  const { t } = useTranslation();
  const note = view.data;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col gap-2 rounded-xl border bg-card p-4 text-left transition-colors hover:border-primary/40 hover:bg-muted/30"
    >
      <span className="flex items-center gap-2 font-medium">
        {note.pinned && <Pin className="size-3.5 text-primary" aria-label={t('Pinned')} />}
        <span className="truncate">{note.title}</span>
        <PendingDot pending={view.pending} />
      </span>
      <span className="line-clamp-4 text-sm whitespace-pre-line text-muted-foreground">
        {note.body || t('No content')}
      </span>
      <span className="mt-auto flex flex-wrap items-center gap-1 pt-1">
        {note.tags.map((tag) => (
          <Badge key={tag} variant="secondary">
            {tag}
          </Badge>
        ))}
        <span className="ml-auto text-xs text-muted-foreground">{formatTimestamp(view.updated_at)}</span>
      </span>
    </button>
  );
}
